import { useMemo } from "react";
import { useSelector } from "react-redux";
import { stringFormatter } from "@kivunova/kivufrontendcommon";
import { DashboardData } from "../../components/Dashboard/types/dashboardTypes";
import { buildProductItemStateMovement } from "../../components/Dashboard/utils/productItemStateMovement";
import { useCompanyDashboardAnalytics } from "./useCompanyDashboardAnalytics";
import { RootState } from "../../store/store";
import { UserRole } from "../../components/Profile/enums/profileEnums";
import { formatCalendarDateInAppTimeZone } from "../../Utils/appTimeZone";

/** Fallback KPI values when API is loading, failed, or returned non-OK. No hardcoded fake numbers. */
const defaultKpis = [
  {
    id: "total_revenue",
    title: "Total Revenue",
    value: "—",
    trend: 0,
    positive: true,
    spark: [0]
  },
  {
    id: "vat_to_pay",
    title: "VAT to pay",
    value: "—",
    trend: 0,
    positive: true,
    spark: [0]
  },
  {
    id: "net_revenue",
    title: "Net Revenue",
    value: "—",
    trend: 0,
    positive: true,
    spark: [0]
  },
  {
    id: "net_profit_margin",
    title: "Net Profit Margin",
    value: "—",
    trend: 0,
    positive: true,
    spark: [0]
  }
];

const defaultProductKpis = [
  {
    id: "total_products",
    title: "Total Products",
    value: "—",
    trend: 0,
    positive: true
  },
  {
    id: "low_stock_products",
    title: "Low Stock Products",
    value: "—",
    trend: 0,
    positive: true
  },
  {
    id: "out_of_stock_products",
    title: "Out of Stock",
    value: "—",
    trend: 0,
    positive: true
  },
  {
    id: "most_stock_products",
    title: "In Stock",
    value: "—",
    trend: 0,
    positive: true
  }
];

const defaultEmployeeKpis = [
  {
    id: "total_employee",
    title: "Total",
    value: "—",
    trend: 0,
    positive: true
  },
  {
    id: "active_employee",
    title: "Active",
    value: "—",
    trend: 0,
    positive: true
  },
  {
    id: "inactive_employee",
    title: "Invited",
    value: "—",
    trend: 0,
    positive: true
  },
  {
    id: "suspended_employee",
    title: "Suspended",
    value: "—",
    trend: 0,
    positive: true
  }
];

const defaultCategoryKpis = [
  {
    id: "total_categories",
    title: "Total Categories",
    value: "—",
    trend: 0,
    positive: true
  },
  {
    id: "active_categories",
    title: "Active Categories",
    value: "—",
    trend: 0,
    positive: true
  },
  {
    id: "deleted_categories",
    title: "Inactive/Deleted",
    value: "—",
    trend: 0,
    positive: true
  }
];

const defaultSubscriptionKpis = [
  { id: "sub_status", title: "Status", value: "—", trend: 0, positive: true },
  {
    id: "sub_last_payment",
    title: "Last Payment",
    value: "—",
    trend: 0,
    positive: true
  },
  {
    id: "sub_next_billing",
    title: "Next Billing",
    value: "—",
    trend: 0,
    positive: true
  }
];

const defaultOrderKpis = [
  {
    id: "total_orders",
    title: "Total Orders",
    value: "—",
    trend: 0,
    positive: true
  },
  {
    id: "order_count_in_period",
    title: "Orders in Period",
    value: "—",
    trend: 0,
    positive: true
  },
  {
    id: "paid_completed_orders",
    title: "Paid/Completed Orders",
    value: "—",
    trend: 0,
    positive: true
  },
  {
    id: "total_invoices",
    title: "Total Invoices",
    value: "—",
    trend: 0,
    positive: true
  }
];

const defaultStoreKpis = [
  {
    id: "stores_total",
    title: "Total Stores",
    value: "—",
    trend: 0,
    positive: true
  },
  {
    id: "stores_active",
    title: "Active",
    value: "—",
    trend: 0,
    positive: true
  },
  {
    id: "stores_pending",
    title: "Pending",
    value: "—",
    trend: 0,
    positive: true
  },
  {
    id: "stores_suspended",
    title: "Suspended",
    value: "—",
    trend: 0,
    positive: true
  }
];

const defaultSupplierKpis = [
  {
    id: "suppliers_total",
    title: "Total Suppliers",
    value: "—",
    trend: 0,
    positive: true
  },
  {
    id: "suppliers_active",
    title: "Active",
    value: "—",
    trend: 0,
    positive: true
  },
  {
    id: "suppliers_suspended",
    title: "Suspended",
    value: "—",
    trend: 0,
    positive: true
  }
];

const defaultCustomerKpis = [
  {
    id: "customers_total",
    title: "Total Customers",
    value: "—",
    trend: 0,
    positive: true
  },
  {
    id: "customers_new",
    title: "New in Period",
    value: "—",
    trend: 0,
    positive: true
  },
  {
    id: "customers_active",
    title: "Active in Period",
    value: "—",
    trend: 0,
    positive: true
  },
  {
    id: "customers_repeat",
    title: "Repeat",
    value: "—",
    trend: 0,
    positive: true
  },
  {
    id: "customers_avg_orders",
    title: "Avg Orders/Customer",
    value: "—",
    trend: 0,
    positive: true
  },
  {
    id: "customers_outstanding",
    title: "With Outstanding Invoices",
    value: "—",
    trend: 0,
    positive: true
  }
];

const defaultChartData = [] as { label: string; value: number }[];
const defaultCharts = {
  salesAndInventoryTrend: [] as {
    date: Date;
    sales: number;
    inventory: number;
  }[],
  movement: buildProductItemStateMovement(undefined),
  categories: [] as { label: string; value: number }[],
  employeesByStatus: defaultChartData,
  employeesByRole: defaultChartData,
  storesByStatus: defaultChartData,
  suppliersByStatus: defaultChartData,
  customersByType: defaultChartData,
  ordersByStatus: defaultChartData,
  invoicesByStatus: defaultChartData,
  productsByStatus: defaultChartData,
  categoriesByStatus: defaultChartData
};

const FALLBACK_CURRENCY = "RWF";

function formatCurrency(value: number, currencyCode: string): string {
  const symbol = currencyCode || FALLBACK_CURRENCY;
  if (value >= 1_000_000) return `${symbol} ${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${symbol} ${(value / 1_000).toFixed(1)}K`;
  return `${symbol} ${Math.round(value)}`;
}

function formatCurrencyFull(value: number, currencyCode: string): string {
  const symbol = currencyCode || FALLBACK_CURRENCY;
  const formatted = value.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  });
  return `${symbol} ${formatted}`;
}

function formatPercent(value: number): string {
  return `${Number(value.toFixed(1))}%`;
}

export interface UseDashboardDataResult {
  data: DashboardData;
  isLoading: boolean;
  error: Error | null;
}

/** Company dashboard: single source of truth is getCompanyDashboardAnalytics. Fallbacks only when loading or API error. */
export const useDashboardData = (): UseDashboardDataResult => {
  const metricTimeRange = useSelector(
    (state: RootState) => state.dashboard.metricTimeRange
  );
  const dashboardStoreId = useSelector(
    (state: RootState) => state.dashboard.dashboardStoreId
  );
  const userRoleRaw = useSelector(
    (state: RootState) => state.user.userDetails?.role
  );
  const userRole = String(userRoleRaw ?? "").trim() || undefined;
  const currencyCode =
    useSelector((state: RootState) => state.company.currencyCode) ??
    FALLBACK_CURRENCY;
  const dateFrom = metricTimeRange?.startDate
    ? formatCalendarDateInAppTimeZone(metricTimeRange.startDate)
    : undefined;
  const dateTo = metricTimeRange?.endDate
    ? formatCalendarDateInAppTimeZone(metricTimeRange.endDate)
    : undefined;

  const trimmedDashboardStoreId = dashboardStoreId?.trim() ?? "";
  const storeIdForQuery =
    userRole === UserRole.COMPANY_ADMIN && trimmedDashboardStoreId
      ? trimmedDashboardStoreId
      : undefined;

  const { data, isSuccess, isLoading, error } = useCompanyDashboardAnalytics({
    dateFrom,
    dateTo,
    storeId: storeIdForQuery
  });

  const dashboardData = useMemo((): DashboardData => {
    if (
      !isSuccess ||
      !data?.getCompanyDashboardAnalytics ||
      data.getCompanyDashboardAnalytics.code !== "OK"
    ) {
      return {
        kpis: defaultKpis,
        subscriptionKpis: defaultSubscriptionKpis,
        orderKpis: defaultOrderKpis,
        storeKpis: defaultStoreKpis,
        supplierKpis: defaultSupplierKpis,
        customerKpis: defaultCustomerKpis,
        employeeKpis: defaultEmployeeKpis,
        productKpis: defaultProductKpis,
        categoryKpis: defaultCategoryKpis,
        salesAndInventoryTrend: defaultCharts.salesAndInventoryTrend,
        movement: defaultCharts.movement,
        categories: defaultCharts.categories,
        employeesByStatus: defaultCharts.employeesByStatus,
        employeesByRole: defaultCharts.employeesByRole,
        storesByStatus: defaultCharts.storesByStatus,
        suppliersByStatus: defaultCharts.suppliersByStatus,
        customersByType: defaultCharts.customersByType,
        ordersByStatus: defaultCharts.ordersByStatus,
        invoicesByStatus: defaultCharts.invoicesByStatus,
        productsByStatus: defaultCharts.productsByStatus,
        categoriesByStatus: defaultCharts.categoriesByStatus
      };
    }

    const api = data.getCompanyDashboardAnalytics;
    const order = api.orderAnalytics;
    const product = api.productAnalytics;
    const employee = api.employeeAnalytics;
    const subscription = api.subscriptionAnalytics;
    const store = api.storeAnalytics;
    const supplier = api.supplierAnalytics;
    const customer = api.customerAnalytics;

    const salesAndInventoryTrend = (api.salesInventoryTrend ?? []).map((p) => ({
      date: new Date(`${p.date}T12:00:00`),
      sales: p.sales ?? 0,
      inventory: p.inventory ?? 0
    }));

    const formatDate = (iso: string | undefined | null): string => {
      if (!iso) return "—";
      try {
        const d = new Date(iso);
        return Number.isNaN(d.getTime())
          ? "—"
          : d.toLocaleDateString(undefined, { dateStyle: "medium" });
      } catch {
        return "—";
      }
    };

    const subscriptionStatusColor = (
      status: string | undefined | null
    ): string | undefined => {
      if (!status) return undefined;
      const s = String(status).toUpperCase();
      if (s === "ACTIVE") return "success.main";
      if (s === "CANCELLED" || s === "EXPIRED") return "error.main";
      if (s === "PAUSED") return "#7b1fa2";
      if (s === "PENDING") return "warning.main";
      return undefined;
    };

    const daysUntil = (iso: string | undefined | null): number | null => {
      if (!iso) return null;
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return null;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      d.setHours(0, 0, 0, 0);
      return Math.ceil((d.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    };

    const totalRevenue = order?.totalRevenue ?? 0;
    const vatToPay = order?.vatToPay ?? 0;
    const netRevenue = totalRevenue - vatToPay;
    const netProfitMargin =
      totalRevenue > 0 ? (netRevenue / totalRevenue) * 100 : null;

    const kpis = order
      ? [
          {
            id: "total_revenue",
            title: "Total Revenue",
            value: formatCurrency(totalRevenue, currencyCode),
            valueFull: formatCurrencyFull(totalRevenue, currencyCode),
            trend: 0,
            positive: true,
            spark: [totalRevenue]
          },
          {
            id: "vat_to_pay",
            title: "VAT to pay",
            value: formatCurrency(vatToPay, currencyCode),
            valueFull: formatCurrencyFull(vatToPay, currencyCode),
            trend: 0,
            positive: true,
            spark: [vatToPay]
          },
          {
            id: "net_revenue",
            title: "Net Revenue",
            value: formatCurrency(netRevenue, currencyCode),
            valueFull: formatCurrencyFull(netRevenue, currencyCode),
            trend: 0,
            positive: true,
            spark: [netRevenue]
          },
          {
            id: "net_profit_margin",
            title: "Net Profit Margin",
            value:
              netProfitMargin != null ? formatPercent(netProfitMargin) : "—",
            trend: 0,
            positive: true,
            spark: netProfitMargin != null ? [netProfitMargin] : [0]
          }
        ]
      : defaultKpis;

    const orderKpis = order
      ? [
          {
            id: "total_orders",
            title: "Total Orders",
            value: String(order.totalOrders),
            trend: 0,
            positive: true
          },
          {
            id: "order_count_in_period",
            title: "Orders in Period",
            value: String(order.orderCountInPeriod),
            trend: 0,
            positive: true
          },
          {
            id: "paid_completed_orders",
            title: "Paid/Completed Orders",
            value: String(order.completedOrdersInPeriod),
            trend: 0,
            positive: true
          },
          {
            id: "total_invoices",
            title: "Total Invoices",
            value: String(order.totalInvoices),
            trend: 0,
            positive: true
          }
        ]
      : defaultOrderKpis;

    const productKpis = product
      ? [
          {
            id: "total_products",
            title: "Total Products",
            value: String(product.totalProducts),
            trend: 0,
            positive: true
          },
          {
            id: "total_product_items",
            title: "Total Product Items",
            value: String(product.totalProductItems ?? 0),
            trend: 0,
            positive: true
          },
          {
            id: "low_stock_products",
            title: "Low Stock Products",
            value: "—",
            trend: 0,
            positive: true
          },
          {
            id: "out_of_stock_products",
            title: "Out of Stock",
            value: String(
              (product.productItemsByState ?? []).find((x) => x.key === "SOLD")
                ?.value ?? 0
            ),
            trend: 0,
            positive: true
          },
          {
            id: "most_stock_products",
            title: "In Stock",
            value: String(product.inStockProductItems),
            trend: 0,
            positive: true
          }
        ]
      : defaultProductKpis;

    const categoryKpis = product
      ? [
          {
            id: "total_categories",
            title: "Total Categories",
            value: String(product.totalCategories),
            trend: 0,
            positive: true
          },
          {
            id: "active_categories",
            title: "Active Categories",
            value: String(product.activeCategories),
            trend: 0,
            positive: true
          },
          {
            id: "deleted_categories",
            title: "Inactive/Deleted",
            value: String(product.totalCategories - product.activeCategories),
            trend: 0,
            positive: true
          }
        ]
      : defaultCategoryKpis;

    const employeeKpis = employee
      ? [
          {
            id: "total_employee",
            title: "Total",
            value: String(employee.totalEmployees),
            trend: 0,
            positive: true
          },
          {
            id: "active_employee",
            title: "Active",
            value: String(employee.activeEmployees),
            trend: 0,
            positive: true
          },
          {
            id: "inactive_employee",
            title: "Invited",
            value: String(
              (employee.employeesByStatus ?? []).find(
                (x) => x.key === "INVITED"
              )?.value ?? 0
            ),
            trend: 0,
            positive: true
          },
          {
            id: "suspended_employee",
            title: "Suspended",
            value: String(
              (employee.employeesByStatus ?? []).find(
                (x) => x.key === "SUSPENDED"
              )?.value ?? 0
            ),
            trend: 0,
            positive: true
          }
        ]
      : defaultEmployeeKpis;

    const nextBillingDays = daysUntil(subscription?.nextBillingAt);
    const subscriptionKpis = subscription
      ? [
          {
            id: "sub_status",
            title: "Status",
            value: subscription.subscriptionStatus
              ? stringFormatter(subscription.subscriptionStatus)
              : "—",
            trend: 0,
            positive: true,
            valueColor: subscriptionStatusColor(subscription.subscriptionStatus)
          },
          {
            id: "sub_last_payment",
            title: "Last Payment",
            value:
              subscription.lastPaymentAmount != null
                ? formatCurrency(subscription.lastPaymentAmount, currencyCode)
                : "—",
            valueFull:
              subscription.lastPaymentAmount != null
                ? formatCurrencyFull(
                    subscription.lastPaymentAmount,
                    currencyCode
                  )
                : undefined,
            trend: 0,
            positive: true
          },
          {
            id: "sub_next_billing",
            title: "Next Billing",
            value: formatDate(subscription.nextBillingAt),
            trend: 0,
            positive: true,
            valueColor:
              nextBillingDays != null && nextBillingDays <= 10
                ? "error.main"
                : undefined
          }
        ]
      : defaultSubscriptionKpis;

    const storeKpis = store
      ? [
          {
            id: "stores_total",
            title: "Total Stores",
            value: String(store.totalStores),
            trend: 0,
            positive: true
          },
          {
            id: "stores_active",
            title: "Active",
            value: String(
              (store.storesByStatus ?? []).find((x) => x.key === "ACTIVE")
                ?.value ?? store.activeStores
            ),
            trend: 0,
            positive: true
          },
          {
            id: "stores_pending",
            title: "Pending",
            value: String(
              (store.storesByStatus ?? []).find((x) => x.key === "PENDING")
                ?.value ?? 0
            ),
            trend: 0,
            positive: true
          },
          {
            id: "stores_suspended",
            title: "Suspended",
            value: String(
              (store.storesByStatus ?? []).find((x) => x.key === "SUSPENDED")
                ?.value ?? 0
            ),
            trend: 0,
            positive: true
          }
        ]
      : defaultStoreKpis;

    const supplierKpis = supplier
      ? [
          {
            id: "suppliers_total",
            title: "Total Suppliers",
            value: String(supplier.totalSuppliers),
            trend: 0,
            positive: true
          },
          {
            id: "suppliers_active",
            title: "Active",
            value: String(
              (supplier.suppliersByStatus ?? []).find((x) => x.key === "ACTIVE")
                ?.value ?? supplier.activeSuppliers
            ),
            trend: 0,
            positive: true
          },
          {
            id: "suppliers_suspended",
            title: "Suspended",
            value: String(
              (supplier.suppliersByStatus ?? []).find(
                (x) => x.key === "SUSPENDED"
              )?.value ?? 0
            ),
            trend: 0,
            positive: true
          }
        ]
      : defaultSupplierKpis;

    const customerKpis = customer
      ? [
          {
            id: "customers_total",
            title: "Total Customers",
            value: String(customer.totalCustomers),
            trend: 0,
            positive: true
          },
          {
            id: "customers_new",
            title: "New in Period",
            value: String(customer.newCustomersInPeriod),
            trend: 0,
            positive: true
          },
          {
            id: "customers_active",
            title: "Active in Period",
            value: String(customer.activeCustomersInPeriod),
            trend: 0,
            positive: true
          },
          {
            id: "customers_repeat",
            title: "Repeat",
            value: String(customer.repeatCustomers),
            trend: 0,
            positive: true
          },
          {
            id: "customers_avg_orders",
            title: "Avg Orders/Customer",
            value:
              customer.averageOrdersPerCustomer != null
                ? customer.averageOrdersPerCustomer.toFixed(1)
                : "—",
            trend: 0,
            positive: true
          },
          {
            id: "customers_outstanding",
            title: "With Outstanding Invoices",
            value: String(customer.customersWithOutstandingInvoices),
            trend: 0,
            positive: true
          }
        ]
      : defaultCustomerKpis;

    const categories: { label: string; value: number }[] = (
      product?.categoriesByStatus ?? []
    ).map((x) => ({ label: stringFormatter(x.key), value: x.value }));

    /** Enrich with standard keys (0 for missing) so comparison charts always show e.g. "In stock vs Out of stock" */
    const toComparisonData = (
      arr: { key: string; value: number }[] | undefined,
      standardKeys: string[]
    ): { label: string; value: number }[] => {
      const map = new Map<string, number>();
      standardKeys.forEach((k) => map.set(k, 0));
      (arr ?? []).forEach((x) => map.set(x.key, x.value));
      return standardKeys.map((k) => ({
        label: stringFormatter(k),
        value: map.get(k) ?? 0
      }));
    };

    const PRODUCT_STATUS_KEYS = [
      "ACTIVE",
      "INACTIVE",
      "DISCONTINUED",
      "DELETED"
    ];
    const CATEGORY_STATUS_KEYS = ["ACTIVE", "INACTIVE", "DELETED"];
    const STORE_STATUS_KEYS = ["ACTIVE", "PENDING", "SUSPENDED", "DELETED"];
    const SUPPLIER_STATUS_KEYS = ["ACTIVE", "SUSPENDED", "DELETED"];
    const EMPLOYEE_STATUS_KEYS = ["INVITED", "ACTIVE", "SUSPENDED"];
    const CUSTOMER_TYPE_KEYS = ["INDIVIDUAL", "COMPANY"];

    const movement = buildProductItemStateMovement(
      product?.productItemsByState
    );

    const employeesByStatus = toComparisonData(
      employee?.employeesByStatus,
      EMPLOYEE_STATUS_KEYS
    );
    const employeesByRole = (employee?.employeesByRole ?? []).map((x) => ({
      label: stringFormatter(x.key),
      value: x.value
    }));
    const storesByStatus = toComparisonData(
      store?.storesByStatus,
      STORE_STATUS_KEYS
    );
    const suppliersByStatus = toComparisonData(
      supplier?.suppliersByStatus,
      SUPPLIER_STATUS_KEYS
    );
    const customersByType = toComparisonData(
      customer?.customersByType,
      CUSTOMER_TYPE_KEYS
    );
    const ordersByStatus = (order?.ordersByStatus ?? []).map((x) => ({
      label: stringFormatter(x.key),
      value: x.value
    }));
    const invoicesByStatus = (order?.invoicesByStatus ?? []).map((x) => ({
      label: stringFormatter(x.key),
      value: x.value
    }));
    const productsByStatus = toComparisonData(
      product?.productsByStatus,
      PRODUCT_STATUS_KEYS
    );
    const categoriesByStatus = toComparisonData(
      product?.categoriesByStatus,
      CATEGORY_STATUS_KEYS
    );

    return {
      kpis,
      subscriptionKpis,
      orderKpis,
      storeKpis,
      supplierKpis,
      customerKpis,
      employeeKpis,
      productKpis,
      categoryKpis,
      salesAndInventoryTrend,
      movement,
      categories: categories.length > 0 ? categories : defaultCharts.categories,
      employeesByStatus,
      employeesByRole:
        employeesByRole.length > 0
          ? employeesByRole
          : defaultCharts.employeesByRole,
      storesByStatus,
      suppliersByStatus,
      customersByType,
      ordersByStatus:
        ordersByStatus.length > 0
          ? ordersByStatus
          : defaultCharts.ordersByStatus,
      invoicesByStatus:
        invoicesByStatus.length > 0
          ? invoicesByStatus
          : defaultCharts.invoicesByStatus,
      productsByStatus,
      categoriesByStatus
    };
  }, [data, isSuccess, currencyCode]);

  return { data: dashboardData, isLoading, error: error ?? null };
};
